import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, pairwise, ReplaySubject, startWith } from 'rxjs';

import { GeneralSettings, PageNavigationItem, PortalConfiguration, WidgetConfigMap, WidgetIdentifier } from '@celum/portals/domain';

import { ConfigurationResolver } from './configuration-resolver';
import { PortalNavigationService } from './portal-navigation.service';
import { ConfigurationService } from '../model/configuration.service';
import { getPageFromPortalConfiguration } from '../util/configuration.util';

@Injectable()
export class ReadonlyConfigurationService implements ConfigurationService {
  public generalSettings$ = new BehaviorSubject<GeneralSettings>(null);
  public portalConfiguration$ = new BehaviorSubject<PortalConfiguration>(null);
  public portalConfiguration = toSignal(this.portalConfiguration$);
  public widgets$ = new ReplaySubject<WidgetIdentifier[]>(1);
  public widgetConfigs$ = new BehaviorSubject<WidgetConfigMap>(null);
  public widgetConfigs = toSignal(this.widgetConfigs$);
  public portalId$ = new BehaviorSubject<string>(null);
  public activePageId$ = new BehaviorSubject<string>(null);
  public activePageId = toSignal(this.activePageId$);
  public activePage$ = new BehaviorSubject<PageNavigationItem>(null);
  public activePage = toSignal(this.activePage$);
  public previousPageSlug$ = new BehaviorSubject<string>(null);

  constructor(configurationResolver: ConfigurationResolver, navigationService: PortalNavigationService) {
    const config = configurationResolver.configuration$.getValue();

    this.portalId$.next(config.id);
    this.portalConfiguration$.next(config);
    this.generalSettings$.next(config.configuration.general.settings);
    this.widgetConfigs$.next(config.configuration.widgetMap);

    this.activePage$.pipe(startWith(null), pairwise()).subscribe(([previousConfig]) => this.previousPageSlug$.next(previousConfig?.slug ?? previousConfig?.id));

    navigationService.activePageId$.subscribe(activePageId => {
      const portalConfiguration = this.portalConfiguration$.getValue();

      const activeNavigationItem = getPageFromPortalConfiguration(portalConfiguration, activePageId);

      this.activePageId$.next(activeNavigationItem.id);
      this.activePage$.next(activeNavigationItem);
      this.widgets$.next([
        ...(portalConfiguration.configuration.general.widgets.header ? [portalConfiguration.configuration.general.widgets.header] : []),
        ...activeNavigationItem.widgets
      ]);
    });
  }

  public selectWidget<C>(widgetId: string, options?: C): void {
    console.warn('You are not in edit mode! "selectWidget" does nothing in readonly mode!');
  }

  public clearSelectedWidget(): void {
    console.warn('You are not in edit mode! "clearSelectedWidget" does nothing in readonly mode!');
  }

  public getPortalId(): string {
    return this.portalId$.value;
  }
}
